import { PixelCrop, PercentCrop } from 'react-image-crop';

type AutoTrimOptions = {
  alphaThreshold?: number;
  padding?: number;
};

type FetchResult = {
  url: string;
  revoke: () => void;
};

const createCanvasContext = (width: number, height: number) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas context not available.');
  }
  return { canvas, context };
};

export const fetchToObjectUrl = async (url: string): Promise<FetchResult> => {
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) {
      throw new Error(`Failed to fetch image. Status: ${response.status}`);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    return {
      url: objectUrl,
      revoke: () => URL.revokeObjectURL(objectUrl),
    };
  } catch (error) {
    console.warn('fetchToObjectUrl: falling back to original URL', error);
    return {
      url,
      revoke: () => {},
    };
  }
};

export const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = (event) => reject(event);
    image.src = url;
  });
};

export const autoTrimTransparentEdges = async (
  url: string,
  options: AutoTrimOptions = {}
): Promise<string> => {
  const { alphaThreshold = 10, padding = 4 } = options;

  const { url: safeUrl, revoke } = await fetchToObjectUrl(url);

  try {
    const image = await loadImage(safeUrl);
    const { context } = createCanvasContext(image.width, image.height);

    context.drawImage(image, 0, 0);

    const { data } = context.getImageData(0, 0, image.width, image.height);

    let minX = image.width;
    let minY = image.height;
    let maxX = 0;
    let maxY = 0;

    for (let y = 0; y < image.height; y += 1) {
      for (let x = 0; x < image.width; x += 1) {
        const alpha = data[(y * image.width + x) * 4 + 3];
        if (alpha >= alphaThreshold) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (minX > maxX || minY > maxY) {
      return url;
    }

    const cropX = Math.max(0, minX - padding);
    const cropY = Math.max(0, minY - padding);
    const cropWidth = Math.min(image.width - cropX, maxX - minX + 1 + padding * 2);
    const cropHeight = Math.min(image.height - cropY, maxY - minY + 1 + padding * 2);

    const { canvas: cropCanvas, context: cropContext } = createCanvasContext(cropWidth, cropHeight);
    cropContext.drawImage(image, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

    return cropCanvas.toDataURL('image/png');
  } finally {
    revoke();
  }
};

export const cropToRect = async (
  url: string,
  crop: PixelCrop,
  percentCrop?: PercentCrop
): Promise<string> => {
  if (!crop || crop.width <= 0 || crop.height <= 0) {
    throw new Error('Invalid crop dimensions.');
  }

  const { url: safeUrl, revoke } = await fetchToObjectUrl(url);

  try {
    const image = await loadImage(safeUrl);

    const naturalWidth = image.naturalWidth;
    const naturalHeight = image.naturalHeight;

    let sourceX: number;
    let sourceY: number;
    let targetWidth: number;
    let targetHeight: number;

    if (percentCrop && percentCrop.width && percentCrop.height) {
      const percentX = typeof percentCrop.x === 'number' ? percentCrop.x : 0;
      const percentY = typeof percentCrop.y === 'number' ? percentCrop.y : 0;

      sourceX = Math.max(0, Math.round((percentX / 100) * naturalWidth));
      sourceY = Math.max(0, Math.round((percentY / 100) * naturalHeight));
      const maxWidth = naturalWidth - sourceX;
      const maxHeight = naturalHeight - sourceY;
      targetWidth = Math.min(
        maxWidth,
        Math.max(1, Math.round((percentCrop.width / 100) * naturalWidth))
      );
      targetHeight = Math.min(
        maxHeight,
        Math.max(1, Math.round((percentCrop.height / 100) * naturalHeight))
      );
    } else {
      const displayWidth = image.width || naturalWidth;
      const displayHeight = image.height || naturalHeight;
      const scaleX = displayWidth ? naturalWidth / displayWidth : 1;
      const scaleY = displayHeight ? naturalHeight / displayHeight : 1;

      sourceX = Math.max(0, Math.floor(crop.x * scaleX));
      sourceY = Math.max(0, Math.floor(crop.y * scaleY));
      const maxWidth = naturalWidth - sourceX;
      const maxHeight = naturalHeight - sourceY;
      targetWidth = Math.min(maxWidth, Math.max(1, Math.round(crop.width * scaleX)));
      targetHeight = Math.min(maxHeight, Math.max(1, Math.round(crop.height * scaleY)));
    }

    const { canvas, context } = createCanvasContext(targetWidth, targetHeight);
    context.drawImage(
      image,
      sourceX,
      sourceY,
      targetWidth,
      targetHeight,
      0,
      0,
      targetWidth,
      targetHeight
    );

    return canvas.toDataURL('image/png');
  } finally {
    revoke();
  }
};
